import React, { useState, useEffect } from "react";
import ReactECharts from "echarts-for-react";
import ChartCard from "../components/ChartCard";
import KpiCard from "../components/KpiCard";
import { post, fmt, COLORS, shortSvc } from "../api";
import { useDashboard } from "../context/DashboardContext";

// Severity helpers
const sevIcon = (s: string) => "";
const sevDot = (s: string) => s === "critical" ? "bg-red-500" : s === "warning" ? "bg-amber-500" : "bg-blue-500";
const sevColor = (s: string) => s === "critical" ? "text-red-400" : s === "warning" ? "text-amber-400" : "text-blue-400";
const sevBorder = (s: string) => s === "critical" ? "border-red-500/30" : s === "warning" ? "border-amber-500/30" : "border-blue-500/30";
const sevBg = (s: string) => s === "critical" ? "bg-red-500/5" : s === "warning" ? "bg-amber-500/5" : "bg-blue-500/5";

function Section({ title, count, icon, defaultOpen, children, borderColor }: {
  title: string; count?: number; icon?: string; defaultOpen?: boolean; children: React.ReactNode; borderColor?: string;
}) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  return (
    <div className={`bg-[#1e2130] border border-[#2a2d3e] ${borderColor ? `border-l-4 ${borderColor}` : ""} rounded-xl overflow-hidden`}>
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-[#252840] transition-colors">
        <div className="flex items-center gap-3">
          {icon && <span className="text-lg">{icon}</span>}
          <span className="font-semibold text-base text-gray-100">{title}</span>
          {count !== undefined && count > 0 && (
            <span className="px-2.5 py-0.5 rounded-full text-xs bg-[#2a2d3e] text-gray-300">{count}</span>
          )}
        </div>
        <span className={`text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}>{"\u25BE"}</span>
      </button>
      {open && <div className="px-5 pb-5 border-t border-[#2a2d3e]">{children}</div>}
    </div>
  );
}

function InsightCard({ severity, title, subtitle, badge, badgeColor, cost, costLabel, children }: {
  severity?: string; title: string; subtitle?: string; badge?: string; badgeColor?: string;
  cost?: string; costLabel?: string; children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`rounded-lg border ${severity ? sevBorder(severity) : "border-[#2a2d3e]"} ${severity ? sevBg(severity) : ""} overflow-hidden transition-all`}>
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#252840]/50 transition-colors">
        <div className="flex items-center gap-2.5 flex-1 min-w-0">
          {severity && <span className={`w-2 h-2 rounded-full shrink-0 ${sevDot(severity)}`} />}
          {badge && <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 ${badgeColor || "text-gray-400 bg-[#2a2d3e]"}`}>{badge}</span>}
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium truncate">{title}</div>
            {subtitle && <div className="text-[11px] text-gray-500 truncate">{subtitle}</div>}
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0 ml-3">
          {cost && (
            <div className="text-right">
              <div className={`text-sm font-semibold tabular-nums ${severity ? sevColor(severity) : "text-gray-300"}`}>{cost}</div>
              {costLabel && <div className="text-[10px] text-gray-500">{costLabel}</div>}
            </div>
          )}
          <span className={`text-gray-500 text-xs transition-transform ${open ? "rotate-180" : ""}`}>{"\u25BE"}</span>
        </div>
      </button>
      {open && <div className="px-4 pb-4 pt-2 border-t border-[#2a2d3e]/50 bg-[#0f1117]/40 space-y-3">{children}</div>}
    </div>
  );
}

function Detail({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-gray-500">{label}</span>
      <span className={`font-medium tabular-nums ${color || "text-gray-300"}`}>{value}</span>
    </div>
  );
}

function ActionTip({ text }: { text: string }) {
  return (
    <div className="flex gap-2 items-start mt-1 p-2.5 rounded-lg bg-blue-500/5 border border-blue-500/10">
      <span className="text-blue-400 shrink-0 text-xs mt-0.5">Tip:</span>
      <span className="text-xs text-blue-300/80 leading-relaxed">{text}</span>
    </div>
  );
}

const wasteInfo: Record<string, { why: string; action: string }> = {
  idle_eip: { why: "Elastic IPs cost money when not attached to a running instance. AWS charges ~$3.65/mo per idle EIP.", action: "Release the EIP if no longer needed, or associate it with an active instance." },
  snapshot_cost: { why: "EBS snapshots accumulate over time. Old snapshots from terminated instances add up.", action: "Review snapshot age and source volume. Delete snapshots older than your retention policy." },
  nat_gateway: { why: "NAT Gateways charge per hour ($0.045/hr) plus data processing ($0.045/GB).", action: "Use VPC endpoints for S3/DynamoDB to bypass NAT. Consider NAT instances for lower-traffic subnets." },
  data_transfer: { why: "Cross-AZ and cross-region data transfer is charged. Misconfigured services can generate unexpected traffic.", action: "Review VPC flow logs. Co-locate communicating services in the same AZ when possible." },
};

function humanizeHealthEvent(eventType: string, service: string): string {
  const et = eventType.toLowerCase();
  if (et.includes("maintenance")) return `Scheduled maintenance for ${service}`;
  if (et.includes("retirement")) return `Hardware retirement affecting ${service}`;
  if (et.includes("degradation")) return `Performance degradation in ${service}`;
  if (et.includes("notification")) return `Service notification for ${service}`;
  if (et.includes("security")) return `Security advisory for ${service}`;
  return `${service}: ${eventType.charAt(0).toUpperCase() + eventType.slice(1)}`;
}

export default function Insights() {
  const { customer, dateParams } = useDashboard();
  const [mom, setMom] = useState<any>(null);
  const [waste, setWaste] = useState<any[]>([]);
  const [forecast, setForecast] = useState<any>(null);
  const [risp, setRisp] = useState<any>(null);
  const [anomalies, setAnomalies] = useState<any[]>([]);
  const [trustedAdvisor, setTrustedAdvisor] = useState<any[]>([]);
  const [healthEvents, setHealthEvents] = useState<any[]>([]);
  const [awsAnomalies, setAwsAnomalies] = useState<any[]>([]);
  const [recommendations, setRecommendations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const dateKey = JSON.stringify(dateParams);
  const insightMonth = dateParams.month || (dateParams.start_date ? dateParams.start_date.slice(0, 7) : "");

  useEffect(() => {
    if (!customer || !insightMonth) return;
    setLoading(true);
    post("/api/insights/all", { customer, month: insightMonth })
      .then((data) => { setMom(data.mom); setWaste(data.waste || []); setForecast(data.forecast); setRisp(data.risp); setAnomalies(data.anomalies || []); })
      .catch(console.error).finally(() => setLoading(false));
    post("/api/insights/trusted-advisor", { customer }).then(setTrustedAdvisor).catch(() => setTrustedAdvisor([]));
    post("/api/insights/health", { customer }).then(setHealthEvents).catch(() => setHealthEvents([]));
    post("/api/insights/aws-anomalies", { customer }).then(setAwsAnomalies).catch(() => setAwsAnomalies([]));
    post("/api/insights/recommendations", { customer, month: insightMonth }).then(setRecommendations).catch(() => setRecommendations([]));
  }, [customer, dateKey]);

  if (loading) return <div className="text-center py-20 text-gray-400">Loading insights...</div>;

  const allAnomalies = [
    ...anomalies.map((a: any) => ({ ...a, source: "CUR Analysis" })),
    ...awsAnomalies.map((a: any) => ({ date: a.start_date, cost: a.total_impact, source: "AWS Detection", description: a.root_causes?.[0] || "Unusual spending", severity: a.severity, root_causes: a.root_causes || [], end_date: a.end_date })),
  ];

  const getRec = (cat: string) => recommendations.find((r: any) => r.category === cat);
  const taRec = getRec("security");
  const costRec = getRec("cost_optimization");
  const perfRec = getRec("performance");
  const whatsNewRec = getRec("whats_new");
  const opsRec = getRec("operational");
  const hasTaFindings = trustedAdvisor.length > 0 && trustedAdvisor[0]?.id !== "no-support";
  const noSupport = trustedAdvisor.length > 0 && trustedAdvisor[0]?.id === "no-support";
  const taSavings = trustedAdvisor.reduce((s: number, t: any) => s + (t.estimated_savings || 0), 0);
  const wasteSavings = waste.reduce((s: number, w: any) => s + (w.monthly_cost || 0), 0);

  return (
    <div className="animate-fadeIn space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Insights &amp; Recommendations</h1>
      </div>

      {mom && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard label={`${mom.current_month} Cost`} value={fmt(mom.total_current)} />
          <KpiCard label={`${mom.previous_month} Cost`} value={fmt(mom.total_previous)} />
          <KpiCard label="MoM Change" value={`${mom.total_change_percent > 0 ? "+" : ""}${mom.total_change_percent}%`} green={mom.total_change_percent < 0} highlight={Math.abs(mom.total_change_percent) > 20} />
          {forecast && <KpiCard label="Next Month Forecast" value={fmt(forecast.projected_next_month)} highlight />}
        </div>
      )}

      {forecast && forecast.months?.length > 1 && (
        <ChartCard title="Cost Trend and Forecast" wide>
          <ReactECharts style={{ height: 280 }} option={{
            backgroundColor: "transparent", tooltip: { trigger: "axis" },
            grid: { left: 80, right: 30, top: 20, bottom: 40 },
            xAxis: { type: "category", data: forecast.months.map((m: any) => m.month), axisLabel: { color: "#9aa0b0" } },
            yAxis: { type: "value", axisLabel: { color: "#9aa0b0" }, splitLine: { lineStyle: { color: "#2a2d3e" } } },
            series: [
              { type: "line", data: forecast.months.map((m: any) => m.type === "actual" ? m.cost : null), smooth: true, lineStyle: { width: 3, color: "#3b82f6" }, areaStyle: { color: { type: "linear", x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: "rgba(59,130,246,0.2)" }, { offset: 1, color: "rgba(59,130,246,0)" }] } }, itemStyle: { color: "#3b82f6" }, symbol: "circle", symbolSize: 6 },
              { type: "line", data: forecast.months.map((m: any, i: number) => m.type === "forecast" ? m.cost : i === forecast.months.length - 2 ? m.cost : null), smooth: true, lineStyle: { width: 3, color: "#f59e0b", type: "dashed" }, itemStyle: { color: "#f59e0b" }, symbol: "diamond", symbolSize: 8 },
            ],
          }} />
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
            <span>Trend: <span className={forecast.trend_direction === "up" ? "text-red-400" : forecast.trend_direction === "down" ? "text-emerald-400" : ""}>{forecast.trend_direction === "up" ? "Increasing" : forecast.trend_direction === "down" ? "Decreasing" : "Stable"}</span></span>
            <span>Confidence: <span className={forecast.confidence === "high" ? "text-emerald-400" : forecast.confidence === "medium" ? "text-amber-400" : "text-gray-500"}>{forecast.confidence}</span></span>
          </div>
        </ChartCard>
      )}

      {mom && mom.by_service?.length > 0 && (
        <Section title="Month-over-Month Changes" count={(mom.top_increases?.length || 0) + (mom.top_decreases?.length || 0)} defaultOpen borderColor="border-l-blue-500">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-3">
            <div>
              <h4 className="text-sm text-gray-400 uppercase tracking-wider font-medium mb-2">Top Increases</h4>
              <div className="space-y-1.5">
                {(mom.top_increases || []).map((c: any, i: number) => (
                  <InsightCard key={i} severity={c.severity} title={shortSvc(c.service)} cost={`+${fmt(c.change_amount)}`} costLabel={`${c.change_percent > 0 ? "+" : ""}${c.change_percent}%`}>
                    <Detail label="This month" value={fmt(c.current_cost)} />
                    <Detail label="Last month" value={fmt(c.previous_cost)} />
                    <Detail label="Difference" value={`+${fmt(c.change_amount)}`} color="text-red-400" />
                    {c.change_percent > 50 && <ActionTip text={`${shortSvc(c.service)} cost jumped ${c.change_percent}%. Check CloudTrail for recent API activity and new resource launches.`} />}
                  </InsightCard>
                ))}
                {(!mom.top_increases || mom.top_increases.length === 0) && <p className="text-xs text-gray-500 py-2">No significant increases</p>}
              </div>
            </div>
            <div>
              <h4 className="text-sm text-gray-400 uppercase tracking-wider font-medium mb-2">Top Decreases</h4>
              <div className="space-y-1.5">
                {(mom.top_decreases || []).map((c: any, i: number) => (
                  <InsightCard key={i} title={shortSvc(c.service)} cost={fmt(c.change_amount)} costLabel={`${c.change_percent}%`}>
                    <Detail label="This month" value={fmt(c.current_cost)} />
                    <Detail label="Last month" value={fmt(c.previous_cost)} />
                    <Detail label="Savings" value={fmt(Math.abs(c.change_amount))} color="text-emerald-400" />
                  </InsightCard>
                ))}
                {(!mom.top_decreases || mom.top_decreases.length === 0) && <p className="text-xs text-gray-500 py-2">No significant decreases</p>}
              </div>
            </div>
          </div>
        </Section>
      )}

      {risp && risp.total_usage_cost > 0 && (
        <Section title="RI and Savings Plan Coverage" defaultOpen borderColor="border-l-emerald-500">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
            <div className="p-3 rounded-lg bg-[#0f1117]">
              <div className="text-xs text-gray-400 mb-1">On-Demand</div>
              <div className={`text-lg font-bold ${risp.on_demand_pct > 60 ? "text-red-400" : risp.on_demand_pct > 40 ? "text-amber-400" : "text-gray-300"}`}>{risp.on_demand_pct}%</div>
              <div className="text-xs text-gray-500">{fmt(risp.on_demand_cost)}</div>
            </div>
            <div className="p-3 rounded-lg bg-[#0f1117]">
              <div className="text-xs text-gray-400 mb-1">RI Coverage</div>
              <div className="text-lg font-bold text-blue-400">{risp.ri_coverage_pct}%</div>
              <div className="text-xs text-gray-500">{fmt(risp.ri_effective_cost)}</div>
            </div>
            <div className="p-3 rounded-lg bg-[#0f1117]">
              <div className="text-xs text-gray-400 mb-1">SP Coverage</div>
              <div className="text-lg font-bold text-emerald-400">{risp.sp_coverage_pct}%</div>
              <div className="text-xs text-gray-500">{fmt(risp.sp_effective_cost)}</div>
            </div>
            <div className="p-3 rounded-lg bg-[#0f1117]">
              <div className="text-xs text-gray-400 mb-1">Unused Commitments</div>
              <div className={`text-lg font-bold ${(risp.ri_unused_cost + risp.sp_unused_commitment) > 0 ? "text-amber-400" : "text-gray-500"}`}>{fmt(risp.ri_unused_cost + risp.sp_unused_commitment)}</div>
            </div>
          </div>
          <div className="mt-3 h-4 rounded-full overflow-hidden bg-[#0f1117] flex">
            {risp.ri_coverage_pct > 0 && <div style={{ width: `${risp.ri_coverage_pct}%` }} className="bg-blue-500" />}
            {risp.sp_coverage_pct > 0 && <div style={{ width: `${risp.sp_coverage_pct}%` }} className="bg-emerald-500" />}
            <div className="flex-1 bg-red-500/20" />
          </div>
          <div className="flex gap-4 mt-1.5 text-[10px] text-gray-500">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" />RI</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" />SP</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500/40" />On-Demand</span>
          </div>
          {risp.on_demand_pct > 50 && <ActionTip text={`${risp.on_demand_pct}% of spend is On-Demand. Savings Plans could save 20-40%.`} />}
          {costRec && (
            <div className="mt-3 pt-3 border-t border-[#2a2d3e]">
              <h4 className="text-sm text-gray-400 uppercase tracking-wider font-medium mb-2">Cost Optimization</h4>
              <div className="space-y-1.5">
                {costRec.items.map((item: string, j: number) => (
                  <div key={j} className="text-sm text-gray-300 leading-relaxed pl-3 border-l-2 border-emerald-500/30 py-0.5">{item}</div>
                ))}
              </div>
            </div>
          )}
        </Section>
      )}

      {(hasTaFindings || taRec || noSupport) && (
        <Section title="Trusted Advisor" count={hasTaFindings ? trustedAdvisor.length : undefined} defaultOpen={hasTaFindings} borderColor="border-l-red-500">
          {noSupport && (
            <div className="mt-3 p-4 rounded-lg bg-amber-500/5 border border-amber-500/20">
              <div className="font-medium text-sm text-amber-400 mb-1">Business or Enterprise Support Required</div>
              <p className="text-xs text-gray-400">Automated Trusted Advisor checks require AWS Business or Enterprise Support.</p>
            </div>
          )}
          {hasTaFindings && taSavings > 0 && (
            <div className="mt-3 p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20 flex items-center justify-between">
              <span className="text-sm text-emerald-400">Total estimated monthly savings</span>
              <span className="text-lg font-bold text-emerald-400">{fmt(taSavings)}/mo</span>
            </div>
          )}
          {hasTaFindings && (
            <div className="mt-3 space-y-1.5">
              {trustedAdvisor.map((ta: any, i: number) => (
                <InsightCard key={i} severity={ta.status === "error" ? "critical" : ta.status === "warning" ? "warning" : "info"} title={ta.name}
                  badge={(ta.category || "").replace(/_/g, " ")}
                  badgeColor={ta.category === "cost_optimizing" ? "text-amber-400 bg-amber-500/10" : ta.category === "security" ? "text-red-400 bg-red-500/10" : "text-blue-400 bg-blue-500/10"}
                  cost={ta.estimated_savings > 0 ? `${fmt(ta.estimated_savings)}/mo` : `${ta.resources_flagged} flagged`}
                  costLabel={ta.estimated_savings > 0 ? "potential savings" : "resources"}>
                  <Detail label="Resources processed" value={String(ta.resources_processed)} />
                  <Detail label="Resources flagged" value={String(ta.resources_flagged)} color={ta.resources_flagged > 0 ? "text-amber-400" : "text-gray-400"} />
                  <Detail label="Check status" value={ta.status} color={ta.status === "error" ? "text-red-400" : "text-amber-400"} />
                  {ta.estimated_savings > 0 && <Detail label="Est. monthly savings" value={fmt(ta.estimated_savings)} color="text-emerald-400" />}
                  <ActionTip text={`Open AWS Console Trusted Advisor and review "${ta.name}" for specific affected resources.`} />
                </InsightCard>
              ))}
            </div>
          )}
          {taRec && !noSupport && (
            <div className="mt-3 pt-3 border-t border-[#2a2d3e]">
              <h4 className="text-sm text-gray-400 uppercase tracking-wider font-medium mb-2">Review Areas</h4>
              <div className="space-y-1.5">
                {taRec.items.map((item: string, j: number) => (
                  <div key={j} className="text-sm text-gray-300 leading-relaxed pl-3 border-l-2 border-red-500/30 py-0.5">{item}</div>
                ))}
              </div>
            </div>
          )}
        </Section>
      )}

      {allAnomalies.length > 0 && (
        <Section title="Cost Anomalies" count={allAnomalies.length} borderColor="border-l-amber-500">
          <div className="mt-3 space-y-1.5">
            {allAnomalies.slice(0, 15).map((a: any, i: number) => (
              <InsightCard key={i} severity={a.severity}
                title={a.source === "CUR Analysis" ? `Unusual daily spend on ${a.date}` : (a.description || "Anomaly detected")}
                subtitle={a.source} badge={a.source}
                badgeColor={a.source === "AWS Detection" ? "text-purple-400 bg-purple-500/10" : "text-gray-400 bg-[#2a2d3e]"}
                cost={fmt(a.cost || a.total_impact)} costLabel={a.source === "CUR Analysis" ? "actual spend" : "total impact"}>
                {a.source === "CUR Analysis" ? (
                  <>
                    <Detail label="Actual cost" value={fmt(a.cost)} />
                    <Detail label="Expected (avg)" value={fmt(a.expected)} />
                    <Detail label="Z-score" value={String(a.z_score)} color={Math.abs(a.z_score) > 3 ? "text-red-400" : "text-amber-400"} />
                    <ActionTip text={`Check CloudTrail and billing console for ${a.date} to identify the spike.`} />
                  </>
                ) : (
                  <>
                    <Detail label="Start date" value={a.date} />
                    {a.end_date && <Detail label="End date" value={a.end_date} />}
                    <Detail label="Total impact" value={fmt(a.cost)} color="text-red-400" />
                    {a.root_causes?.length > 0 && (
                      <div className="mt-1">
                        <span className="text-xs text-gray-500">Root causes:</span>
                        {a.root_causes.map((rc: string, j: number) => (
                          <div key={j} className="text-sm text-gray-300 pl-3 border-l-2 border-amber-500/30">{rc}</div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </InsightCard>
            ))}
          </div>
        </Section>
      )}

      {(waste.length > 0) && (
        <Section title="Potential Waste and Optimization" count={waste.length} borderColor="border-l-amber-500">
          {wasteSavings > 0 && (
            <div className="mt-3 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20 flex items-center justify-between">
              <span className="text-sm text-amber-400">Total potential monthly waste</span>
              <span className="text-lg font-bold text-amber-400">{fmt(wasteSavings)}/mo</span>
            </div>
          )}
          <div className="mt-3 space-y-1.5">
            {waste.map((w: any, i: number) => {
              const info = wasteInfo[w.type] || { why: "This resource may not be needed.", action: "Review and remove if unused." };
              const shortRid = w.resource_id?.length > 60 ? "..." + w.resource_id.slice(-50) : w.resource_id;
              return (
                <InsightCard key={i} severity={w.severity} title={w.description}
                  badge={shortSvc(w.service)} badgeColor="text-gray-400 bg-[#2a2d3e]"
                  cost={`${fmt(w.monthly_cost)}/mo`} costLabel="monthly waste">
                  <Detail label="Resource" value={shortRid} />
                  <Detail label="Monthly cost" value={fmt(w.monthly_cost)} color="text-amber-400" />
                  <Detail label="Annual impact" value={fmt(w.monthly_cost * 12)} color="text-red-400" />
                  <div className="mt-2 p-2.5 rounded-lg bg-[#0f1117]/80 space-y-1.5">
                    <div className="text-xs text-gray-400"><span className="text-gray-500">Why: </span>{info.why}</div>
                    <div className="text-xs text-blue-300/80"><span className="text-gray-500">Action: </span>{info.action}</div>
                  </div>
                </InsightCard>
              );
            })}
          </div>
        </Section>
      )}

      {healthEvents.length > 0 && (
        <Section title="AWS Health Events" count={healthEvents.length} borderColor="border-l-purple-500">
          <div className="mt-3 space-y-1.5">
            {healthEvents.map((he: any, i: number) => (
              <InsightCard key={i} severity={he.severity}
                title={humanizeHealthEvent(he.event_type, he.service)}
                badge={he.status} badgeColor={he.status === "Active" ? "text-red-400 bg-red-500/10" : "text-amber-400 bg-amber-500/10"}
                cost={he.start_time?.split("T")[0]} costLabel={he.status === "Active" ? "started" : "scheduled"}>
                <Detail label="Service" value={he.service || "unknown"} />
                <Detail label="Region" value={he.region || "global"} />
                <Detail label="Status" value={he.status} color={he.status === "Active" ? "text-red-400" : "text-amber-400"} />
                {he.end_time && <Detail label="Expected end" value={new Date(he.end_time).toLocaleString()} />}
                {he.severity === "critical" && <ActionTip text="Active issue. Check AWS Health Dashboard for updates." />}
                {he.severity === "warning" && <ActionTip text="Scheduled change. Review affected resources and plan maintenance." />}
              </InsightCard>
            ))}
          </div>
        </Section>
      )}

      {perfRec && (
        <Section title={perfRec.title} borderColor="border-l-blue-500">
          <div className="mt-3 space-y-2">
            {perfRec.items.map((item: string, j: number) => {
              const [svcPart, ...rest] = item.split(": ");
              return (
                <div key={j} className="p-3 rounded-lg bg-[#0f1117]/50 border border-[#2a2d3e]">
                  {rest.length > 0 ? (<><div className="text-xs text-blue-400 font-medium mb-1">{svcPart}</div><div className="text-sm text-gray-300 leading-relaxed">{rest.join(": ")}</div></>) : (<div className="text-sm text-gray-300 leading-relaxed">{item}</div>)}
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {whatsNewRec && (
        <Section title={whatsNewRec.title} borderColor="border-l-purple-500">
          <div className="mt-3 space-y-2">
            {whatsNewRec.items.map((item: string, j: number) => {
              const [svcPart, ...rest] = item.split(": ");
              return (
                <div key={j} className="p-3 rounded-lg bg-[#0f1117]/50 border border-[#2a2d3e]">
                  {rest.length > 0 ? (<><div className="text-xs text-purple-400 font-medium mb-1">{svcPart}</div><div className="text-sm text-gray-300 leading-relaxed">{rest.join(": ")}</div></>) : (<div className="text-sm text-gray-300 leading-relaxed">{item}</div>)}
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {opsRec && (
        <Section title={opsRec.title} borderColor="border-l-emerald-500">
          <div className="mt-3 space-y-2">
            {opsRec.items.map((item: string, j: number) => (
              <div key={j} className="p-3 rounded-lg bg-[#0f1117]/50 border border-[#2a2d3e]">
                <div className="text-sm text-gray-300 leading-relaxed">{item}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {!mom && !loading && (
        <div className="text-center py-20 text-gray-500">Select a customer and date range to view insights</div>
      )}
    </div>
  );
}
